import React, { useCallback, useEffect, useMemo, useState } from 'react';
import io from 'socket.io-client';
import { auditFetch } from '../utils/auditFetch';
import '../styles/Dashboard.css';

const typeLabels = {
  patient_waiting_room: 'Sala de espera',
  clinical_file_uploaded: 'Archivo',
  appointment_scheduled: 'Cita',
  referral_pending: 'Referencia',
};

const InternalNotificationsBell = ({ user }) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  const auditActor = useMemo(() => ({
    id: user.id,
    role: user.role,
    name: user.name,
  }), [user.id, user.role, user.name]);

  const loadNotifications = useCallback(() => {
    const params = new URLSearchParams({
      userId: user.id,
      role: user.role,
      limit: '40',
    });
    auditFetch(`${backendUrl}/api/internal-notifications?${params.toString()}`, {}, auditActor)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('No se pudieron cargar notificaciones'))))
      .then((data) => setNotifications(Array.isArray(data.notifications) ? data.notifications : []))
      .catch((error) => {
        console.error('Error loading internal notifications:', error);
        setStatusMessage('No se pudieron cargar las notificaciones.');
      });
  }, [auditActor, backendUrl, user.id, user.role]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    const socket = io(backendUrl || undefined);
    socket.on('connect', () => {
      socket.emit('user:online', {
        userId: user.id,
        role: user.role,
      });
    });
    socket.on('notification:created', (notification) => {
      setNotifications((current) => {
        if (current.some((item) => item.id === notification.id)) return current;
        return [notification, ...current].slice(0, 40);
      });
    });
    return () => socket.disconnect();
  }, [backendUrl, user.id, user.role]);

  const unreadCount = notifications.filter((notification) => notification.status !== 'read').length;

  const markRead = async (notification) => {
    if (notification.status === 'read') return;
    try {
      const response = await auditFetch(`${backendUrl}/api/internal-notifications/${notification.id}/read`, {
        method: 'PATCH',
      }, auditActor);
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudo marcar como leida');
      setNotifications((current) => current.map((item) => (
        item.id === notification.id ? data.notification : item
      )));
    } catch (error) {
      console.error('Error marking notification read:', error);
      setStatusMessage('No se pudo marcar la notificacion.');
    }
  };

  const markAllRead = async () => {
    try {
      const response = await auditFetch(`${backendUrl}/api/internal-notifications/read-all`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, role: user.role }),
      }, auditActor);
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudieron marcar las notificaciones');
      setNotifications((current) => current.map((notification) => ({
        ...notification,
        status: 'read',
        readAt: notification.readAt || new Date().toISOString(),
      })));
    } catch (error) {
      console.error('Error marking all notifications read:', error);
      setStatusMessage('No se pudieron marcar las notificaciones.');
    }
  };

  return (
    <div className="internal-notifications">
      <button
        type="button"
        className="notifications-trigger"
        onClick={() => setIsOpen((current) => !current)}
        aria-label="Notificaciones internas"
      >
        <span>Alertas</span>
        {unreadCount > 0 && <strong>{unreadCount}</strong>}
      </button>

      {isOpen && (
        <div className="notifications-popover">
          <div className="notifications-header">
            <div>
              <h3>Notificaciones internas</h3>
              <p>{unreadCount} pendiente(s)</p>
            </div>
            <button type="button" onClick={markAllRead} disabled={unreadCount === 0}>
              Marcar leidas
            </button>
          </div>
          {statusMessage && <p className="notifications-message">{statusMessage}</p>}
          <div className="notifications-list">
            {notifications.length === 0 ? (
              <p className="notifications-empty">Sin notificaciones internas.</p>
            ) : (
              notifications.map((notification) => (
                <button
                  type="button"
                  key={notification.id}
                  className={`notification-item ${notification.status === 'read' ? 'read' : 'unread'}`}
                  onClick={() => markRead(notification)}
                >
                  <span>{typeLabels[notification.type] || 'Aviso'}</span>
                  <strong>{notification.title}</strong>
                  <small>{notification.message}</small>
                  <em>{new Date(notification.createdAt).toLocaleString('es-EC')}</em>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InternalNotificationsBell;
